import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import UserSidebar from '../../components/sidebar/UserSidebar';
import { AppDispatch, RootState } from '../../redux/store';
import { fetchPaymentById } from '../../redux/paymentSlice';
import StatusBadge from '../../components/StatusBadge';
import { paymentsApi } from '../../services/api';
import './PaymentDetailsPage.css';

const PaymentDetailsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { paymentId } = useParams<{ paymentId: string }>();
  const { currentPayment, loading, error } = useSelector(
    (state: RootState) => state.payment
  );

  useEffect(() => {
    if (paymentId) {
      dispatch(fetchPaymentById(paymentId));
    }
  }, [dispatch, paymentId]);

  const formatCurrency = (amount: number, currencyCode: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const openFreshInvoice = async () => {
    try {
      if (!paymentId) return;
      const { invoiceUrl } = await paymentsApi.getPaymentInvoiceUrl(paymentId);
      window.open(invoiceUrl, '_blank');
    } catch (err) {
      console.error('Failed to open invoice:', err);
      alert('Failed to load invoice. Please try again.');
    }
  };

  const downloadFreshInvoice = async () => {
    try {
      if (!paymentId) return;
      const { invoiceUrl, fileName } = await paymentsApi.getPaymentInvoiceUrl(paymentId);
      const link = document.createElement('a');
      link.href = invoiceUrl;
      link.download = fileName || `invoice-${paymentId}.pdf`;
      link.target = '_blank';
      link.rel = 'noreferrer';
      link.click();
    } catch (err) {
      console.error('Failed to download invoice:', err);
      alert('Failed to download invoice. Please try again.');
    }
  };

  if (loading) {
    return (
      <DashboardLayout sidebar={<UserSidebar />}>
        <div className="payment-details-page">
          <div className="loading">Loading payment details...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !currentPayment) {
    return (
      <DashboardLayout sidebar={<UserSidebar />}>
        <div className="payment-details-page">
          <div className="error">
            Failed to load payment details.
            <button type="button" onClick={() => navigate(-1)}>Go Back</button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebar={<UserSidebar />}>
      <div className="payment-details-page">
        <div className="page-header">
          <h2>Payment Details</h2>
          <p>Review your payment request and status</p>
        </div>

        <div className="payment-details">
          <div className="detail-section">
            <h3>Request Summary</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Recipient</label>
                <span>{currentPayment.recipientCompany}</span>
              </div>
              <div className="detail-item">
                <label>Account Number</label>
                <span>{currentPayment.accountNumber}</span>
              </div>
              <div className="detail-item">
                <label>Country</label>
                <span>{currentPayment.recipientBankCountry}</span>
              </div>
              <div className="detail-item">
                <label>Status</label>
                <span><StatusBadge status={currentPayment.status} type="payment" /></span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Amounts</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Foreign Amount</label>
                <span>{formatCurrency(currentPayment.foreignAmount, currentPayment.foreignCurrency)}</span>
              </div>
              <div className="detail-item">
                <label>Local Amount (NGN)</label>
                <span>{formatCurrency(currentPayment.localAmount, 'NGN')}</span>
              </div>
              <div className="detail-item">
                <label>Exchange Rate</label>
                <span>{currentPayment.exchangeRate}</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Timing</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Submitted</label>
                <span>{currentPayment.createdAt ? new Date(currentPayment.createdAt).toLocaleString() : 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Updated</label>
                <span>{
                  (currentPayment as any).updatedAt
                    ? new Date((currentPayment as any).updatedAt).toLocaleString()
                    : 'N/A'
                }</span>
              </div>
            </div>
          </div>

          {currentPayment.invoiceUrl && (
            <div className="detail-section">
              <h3>Invoice</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="button" className="view-invoice-btn" onClick={openFreshInvoice}>
                  View Invoice
                </button>
                <button type="button" className="view-invoice-btn" onClick={downloadFreshInvoice}>
                  Download Invoice
                </button>
              </div>
            </div>
          )}

          <div className="detail-section">
            <h3>Uploaded Documents</h3>
            <div className="documents-section">
              <div className="documents-list">
                {currentPayment.invoiceUrl ? (
                  <div className="document-item">
                    <div className="document-info">
                      <span className="document-name">
                        {currentPayment.invoiceOriginalFileName || currentPayment.invoiceFileName || 'Invoice'}
                      </span>
                      <span className="document-meta">
                        {currentPayment.invoiceMimeType || 'Document'}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="view-document-btn"
                      onClick={openFreshInvoice}
                    >
                      View Document
                    </button>
                  </div>
                ) : (
                  <p className="empty-documents">No documents uploaded.</p>
                )}
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Approval Audit Trail</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Approval Status</label>
                <span>{(currentPayment as any).approvalStatus || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Approved By</label>
                <span>{(currentPayment as any).approvedBy?.name || (currentPayment as any).approvedBy || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Approval Notes</label>
                <span>{(currentPayment as any).approvalNotes || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Approved At</label>
                <span>{
                  (currentPayment as any).approvedAt
                    ? new Date((currentPayment as any).approvedAt).toLocaleString()
                    : 'N/A'
                }</span>
              </div>
            </div>
          </div>
        </div>

        <div className="back-button">
          <button type="button" onClick={() => navigate(-1)}>Back to Payments</button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PaymentDetailsPage;
